import classnames from 'classnames';
import type { ComponentProps, ElementType, ReactNode } from 'react';
import type { To } from 'react-router';
import {
  Link as RRDLink, // eslint-disable-line no-restricted-imports
  NavLink as RRDNavLink, // eslint-disable-line no-restricted-imports
  matchPath,
  useLocation,
  useParams,
  useSearchParams,
} from 'react-router';

import { Navigate } from './Navigate';
import { NotFoundContext } from './NotFoundContext';
import { RedirectToParent, standInForPagesHandle } from './RedirectToParent';
import { ReplaceAndRedirect } from './ReplaceAndRedirect';
import { PrefetchLink, usePrefetch } from './prefetch';

export { getRoutesFromFiles, toRoutePath } from './autoroute';
export {
  Navigate,
  NotFoundContext,
  PrefetchLink,
  RedirectToParent,
  ReplaceAndRedirect,
  standInForPagesHandle,
};
export { isNotFoundError } from './isNotFoundError';

let shouldReloadUponNavigation = false;
export const updateShouldReloadUponNavigation = (value: boolean) => {
  shouldReloadUponNavigation = value;
};

export type LinkProps<P extends ElementType> = Omit<
  ComponentProps<P>,
  'viewTransition'
> & {
  keepSearchParams?: boolean;
  prefetch?: Parameters<typeof usePrefetch>[1];
};

const IS_VITEST = import.meta.env.NODE_ENV === 'test';
const useResolvedTo = (to: To, keepSearchParams?: boolean) => {
  const [searchParams] = useSearchParams();
  if (typeof to !== 'string') {
    return to;
  }
  const toWithSearchParams = keepSearchParams
    ? `${to}?${searchParams.toString()}`
    : to;
  return toWithSearchParams;
};

// There is some repetition here between Link and NavLink, but remix hasn't found a way around that either.
export const Link = ({
  to,
  keepSearchParams,
  prefetch,
  ref,
  ...rest
}: LinkProps<typeof RRDLink>) => {
  const resolvedTo = useResolvedTo(to, keepSearchParams);
  const [mergedRef, shouldPrefetch] = usePrefetch<HTMLAnchorElement>(
    ref,
    prefetch
  );
  return (
    <>
      <RRDLink
        ref={mergedRef}
        // WARNING to=".." behaves differently than to={{pathname: '..'}}
        to={resolvedTo}
        viewTransition={!IS_VITEST}
        reloadDocument={shouldReloadUponNavigation}
        {...rest}
      />
      {!!shouldPrefetch && <PrefetchLink to={resolvedTo} />}
    </>
  );
};

type NavLinkProps = LinkProps<typeof RRDNavLink> & {
  matches?: Nullable<string>[];
};

export const NavLink = ({
  to,
  keepSearchParams,
  prefetch,
  matches,
  className,
  ref,
  children,
  ...rest
}: NavLinkProps) => {
  const resolvedTo = useResolvedTo(to, keepSearchParams);
  const [mergedRef, shouldPrefetch] = usePrefetch<HTMLAnchorElement>(
    ref,
    prefetch
  );

  const location = useLocation();
  const computedIsActive = !!matches?.some(
    pathname => pathname && matchPath(pathname, location.pathname)
  );

  return (
    <>
      <RRDNavLink
        ref={mergedRef}
        // WARNING to=".." behaves differently than to={{pathname: '..'}}
        to={resolvedTo}
        viewTransition={!IS_VITEST}
        reloadDocument={shouldReloadUponNavigation}
        {...rest}
        className={classnames(className, { active: computedIsActive })}
      >
        {typeof children === 'function'
          ? ({ isActive, isPending, isTransitioning }): ReactNode =>
              children({
                isActive: isActive || computedIsActive,
                isPending,
                isTransitioning,
              })
          : children}
      </RRDNavLink>
      {!!shouldPrefetch && <PrefetchLink to={resolvedTo} />}
    </>
  );
};

export const useTypedParams = <T extends Record<string, string>>() => {
  return useParams() as T;
};

type CatchAll<T extends string> = T extends `${string}/` ? `${T}*` : `${T}/*`;

export function catchAll<const T extends string>(path: T): CatchAll<T> {
  return `${path.replace(/\/$/, '')}/*` as CatchAll<T>;
}
